import { Box, Button, Flex, GmgTheme } from '@gmg/gmg-react-components';
import { FunctionComponent, useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';
import PictoStormDark from '../../assets/svg/graphic_cloud-storm-dark-255.svg';
import PictoStormLight from '../../assets/svg/graphic_cloud-storm-light-255.svg';
import PictoLockDark from '../../assets/svg/graphic_lock-dark-255.svg';
import PictoLockLight from '../../assets/svg/graphic_lock-light-255.svg';
import AppContext from 'src/AppContext';

export type ApplicationError = 'unauthorized' | 'userWithoutOrg' | 'unhandled';

interface ErrorMsgProps {
    errorType: ApplicationError;
}

// Think about moving this to gmg-react-components
const ErrorMsg: FunctionComponent<ErrorMsgProps> = props => {
    const { t } = useTranslation();
    const theme: GmgTheme = useTheme();
    const context = useContext(AppContext);

    const getErrorContent = (error: ApplicationError) => {
        switch (error) {
            case 'unauthorized':
                return ({
                    imageDark: <PictoLockDark width={255} height={255} />,
                    imageLight: <PictoLockLight width={255} height={255} />,
                    title: t('ErrorMsg.unauthorizedTitle', 'Oops, it seems like you don\'t have access to this page.'),
                    subtitle: t('ErrorMsg.unauthorizedSub', 'The page you are trying to access has restricted permissions.'),
                });

            case 'userWithoutOrg':
                return ({
                    imageDark: <PictoLockDark width={255} height={255} />,
                    imageLight: <PictoLockLight width={255} height={255} />,
                    title:
                        <Flex flexDirection='column'>
                            <Trans i18nKey='ErrorMsg.userWithoutOrgTitle'>
                                The following email address is not linked to an organisation:
                            </Trans>
                            <Email>{context?.user?.email || ' '}</Email>
                        </Flex>,
                    subtitle: (
                        <StyledParagraph>
                            <Trans
                                i18nKey="ErrorMsg.userWithoutOrgSub"
                                components={{ bold: <strong />, para: <StyledParagraph /> }}
                            />
                        </StyledParagraph>
                    ),
                });

            default:
                return ({
                    imageDark: <PictoStormDark width={255} height={255} />,
                    imageLight: <PictoStormLight width={255} height={255} />,
                    title: t('ErrorMsg.unhandledTitle', 'Oh no, we are sorry! It looks like something went wrong.'),
                    subtitle: t('ErrorMsg.unhandledSub', 'We are working on the problem, please try again later.'),
                });
        }
    };

    const content = getErrorContent(props.errorType);

    return (
        <Flex height="100%" bg="backgroundPaper" justifyContent="center">
            <Div>
                {theme.type === 'dark' ? content.imageDark : content.imageLight}
                <h1>{content.title}</h1>
                <Box className="px14_400_contrast" pt="24px">
                    {content.subtitle}
                    <Trans t={t} i18nKey={'ErrorMsg.support'}>
                        For further assistance, please contact our support team:
                        <a
                            href="https://gmgcolor.com/support"
                            target="_blank"
                            rel="noreferrer"
                        >
                        </a>
                    </Trans>
                </Box>
                <Flex pt="24px">
                    <Button variant={'primary'} size='big' onClick={() => window.open(
                        context.language === 'de' ? 'https://gmgcolor.com/de/support' : 'https://gmgcolor.com/support',
                        '_blank')}>
                        {t('UserProfile.contact_support_button', 'Contact Support')}
                    </Button>
                </Flex>
            </Div>
        </Flex>
    );
};

const StyledParagraph = styled.div`
    margin-bottom: 16px;
`;

const Email = styled.span`
    font-size: 30px;
    font-weight: 400;
`;

const Div = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    line-height: 1.4;
`;

export default ErrorMsg;